import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
/**
 * UserProfileBuilders — statically imported by index.tsx.
 *
 * Contains ONLY the render helpers (buildHero, buildSidebar, buildUserPhoneNav)
 * that are called inside the UserPage extend() override registered at app boot.
 * Keeping these separate prevents the heavy AvocadoUser*Page classes from
 * being pulled into the main bundle via the static import in index.tsx.
 *
 * The page components (AvocadoUserPostsPage etc.) live in UserProfilePage.tsx
 * and are loaded lazily via dynamic import() only when the user navigates to
 * a profile route.
 */
import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import Avatar from 'flarum/common/components/Avatar';
import AvatarEditor from 'flarum/forum/components/AvatarEditor';
import Dropdown from 'flarum/common/components/Dropdown';
import listItems from 'flarum/common/helpers/listItems';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import humanTime from 'flarum/common/helpers/humanTime';
import { coverPosition, formatDate, safeCssUrl, trans } from '../utils';
import { bone, BONE_FILL } from './Skeletons';

// ─── Scrollable nav ───────────────────────────────────────────────────────────

class ScrollableNav {
  constructor() {
    _defineProperty(this, "_el", null);
    _defineProperty(this, "_canLeft", false);
    _defineProperty(this, "_canRight", false);
    _defineProperty(this, "_dragging", false);
    _defineProperty(this, "_startX", 0);
    _defineProperty(this, "_scrollLeft0", 0);
    _defineProperty(this, "_ro", null);
    _defineProperty(this, "_handleScroll", null);
    _defineProperty(this, "_handleMouseDown", null);
    _defineProperty(this, "_handleMouseMove", null);
    _defineProperty(this, "_handleMouseUp", null);
  }
  _check() {
    const el = this._el;
    if (!el) return;
    const l = el.scrollLeft > 1;
    const r = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (l !== this._canLeft || r !== this._canRight) {
      this._canLeft = l;
      this._canRight = r;
      m.redraw();
    }
  }
  _scroll(dir) {
    this._el?.scrollBy({
      left: dir * 160,
      behavior: 'smooth'
    });
  }
  oncreate(vnode) {
    const el = vnode.dom.querySelector('.AvocadoUserPage-navInner');
    this._el = el;
    if (!el) return;
    this._handleScroll = () => this._check();
    this._handleMouseDown = e => {
      this._dragging = true;
      this._startX = e.pageX - el.offsetLeft;
      this._scrollLeft0 = el.scrollLeft;
      document.documentElement.style.cursor = 'grabbing';
      document.documentElement.style.userSelect = 'none';
    };
    this._handleMouseMove = e => {
      if (!this._dragging) return;
      e.preventDefault();
      el.scrollLeft = this._scrollLeft0 - (e.pageX - el.offsetLeft - this._startX);
    };
    this._handleMouseUp = () => {
      if (!this._dragging) return;
      this._dragging = false;
      document.documentElement.style.cursor = '';
      document.documentElement.style.userSelect = '';
    };
    el.addEventListener('scroll', this._handleScroll, {
      passive: true
    });
    el.addEventListener('mousedown', this._handleMouseDown);
    window.addEventListener('mousemove', this._handleMouseMove);
    window.addEventListener('mouseup', this._handleMouseUp);
    this._ro = new ResizeObserver(() => this._check());
    this._ro.observe(el);
    this._check();
    const activeItem = el.querySelector('li.active');
    if (activeItem) {
      requestAnimationFrame(() => {
        el.scrollLeft = activeItem.offsetLeft - (el.clientWidth - activeItem.clientWidth) / 2;
      });
    }
  }
  onremove() {
    if (this._el) {
      this._el.removeEventListener('scroll', this._handleScroll);
      this._el.removeEventListener('mousedown', this._handleMouseDown);
    }
    window.removeEventListener('mousemove', this._handleMouseMove);
    window.removeEventListener('mouseup', this._handleMouseUp);
    this._ro?.disconnect();
    this._el = null;
  }
  view(vnode) {
    return m("div", {
      className: "AvocadoUserPage-nav"
    }, m("button", {
      className: `AvocadoUserPage-navArrow AvocadoUserPage-navArrow--left${this._canLeft ? ' is-visible' : ''}`,
      onclick: () => this._scroll(-1),
      "aria-label": extractText(app.translator.trans('ramon-avocado.forum.profile.scroll_left')),
      tabindex: "-1"
    }, m("i", {
      className: "fas fa-chevron-left",
      "aria-hidden": "true"
    })), vnode.children, m("button", {
      className: `AvocadoUserPage-navArrow AvocadoUserPage-navArrow--right${this._canRight ? ' is-visible' : ''}`,
      onclick: () => this._scroll(1),
      "aria-label": extractText(app.translator.trans('ramon-avocado.forum.profile.scroll_right')),
      tabindex: "-1"
    }, m("i", {
      className: "fas fa-chevron-right",
      "aria-hidden": "true"
    })));
  }
}

// ─── Shared: hero ─────────────────────────────────────────────────────────────

/** O componente de bio do fof/user-bio, se a extensão estiver ativa. */
const userBioComponent = () => {
  try {
    return flarum.reg.get('fof-user-bio', 'forum/components/UserBio') || null;
  } catch {
    return null;
  }
};
export function buildHero(user, isEditable, controls) {
  if (controls === void 0) {
    controls = [];
  }
  if (!user) {
    // A mesma marcação do hero de verdade (abaixo), com ossos no lugar do
    // texto: avatar, nome, @handle, meta e o botão de controles ficam onde vão
    // estar, em qualquer largura. Bio e selos não entram — não dá para saber se
    // o usuário tem antes de ele carregar.
    return m("div", {
      className: "AvocadoUserPage-hero AvocadoUserPage-hero--skeleton",
      "aria-hidden": "true"
    }, m("div", {
      className: "AvocadoUserPage-cover"
    }), m("div", {
      className: "AvocadoUserPage-hero-inner"
    }, m("div", {
      className: "AvocadoUserPage-hero-bar"
    }, m("div", {
      className: "AvocadoUserPage-hero-avatarWrap"
    }, m("span", {
      className: `Avatar ${BONE_FILL}`
    })), m("div", {
      className: "AvocadoUserPage-hero-identity"
    }, m("div", {
      className: "AvocadoUserPage-hero-nameRow"
    }, m("div", {
      className: "AvocadoUserPage-hero-name"
    }, bone('210px'))), m("span", {
      className: "AvocadoUserPage-hero-username"
    }, bone('112px')), m("div", {
      className: "AvocadoUserPage-hero-meta"
    }, m("span", {
      className: "AvocadoUserPage-metaItem"
    }, bone('188px')), m("span", {
      className: "AvocadoUserPage-metaItem"
    }, bone('64px')))), app.session.user && m("div", {
      className: "AvocadoUserPage-hero-actions"
    }, m("div", {
      className: "ButtonGroup Dropdown"
    }, m("span", {
      className: `Button AvocadoUserPage-controlsBtn ${BONE_FILL}`
    }, m("span", {
      className: "Button-label"
    }, app.translator.trans('core.forum.user_controls.button')), m("i", {
      className: "icon fas fa-caret-down Button-caret"
    }))))), app.session.user?.isAdmin?.() && userBioComponent() && m("div", {
      className: "AvocadoUserPage-hero-bio"
    }, m("div", {
      className: "UserBio editable"
    }, m("div", {
      className: "UserBio-content"
    }, m("p", {
      className: "UserBio-placeholder"
    }, bone('196px')))))));
  }
  const color = user.color?.() || '#5a6480';
  const badges = user.badges?.().toArray?.() || [];
  const isOnline = user.isOnline?.();
  const lastSeen = user.lastSeenAt?.();
  const username = user.username?.();
  const joinTime = user.joinTime?.();
  const joinLabel = joinTime ? formatDate(new Date(joinTime), {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }) : null;

  // forumaker/profile-cover compat — a extensão estende UserCard.prototype.view,
  // mas o Avocado renderiza um hero próprio (não usa UserCard aqui). Lemos
  // user.cover() diretamente; se a extensão não estiver instalada o método
  // não existe e o optional-call retorna undefined. safeCssUrl rejeita
  // protocolos perigosos (data:, javascript:).
  const coverUrl = user.cover?.();
  const coverCss = coverUrl ? safeCssUrl(coverUrl) : null;
  const hasCover = coverCss !== null && coverCss !== 'none';
  const heroStyle = {
    '--user-color': color
  };
  if (hasCover) {
    heroStyle['--user-cover'] = coverCss;
    // Same reason as the cover URL itself: the extension applies the framing to
    // core's .UserCard, which this hero replaces. Without this the slider in the
    // cover editor moved nothing on the profile page.
    heroStyle['--user-cover-position'] = coverPosition(user);
  }
  const onlineLabel = trans('ramon-avocado.forum.user.online', 'Online');
  const UserBio = userBioComponent();
  const bioNode = UserBio && user.attribute('canViewBio') ? m("div", {
    className: "AvocadoUserPage-hero-bio"
  }, m(UserBio, {
    user: user,
    editable: isEditable
  })) : null;

  // Layout estilo Facebook: capa larga, avatar grande sobreposto à esquerda,
  // identidade (nome + @handle + stats) à direita, ações na extrema direita —
  // tudo numa faixa horizontal; a bio fica abaixo, em largura cheia.
  return m("div", {
    className: 'AvocadoUserPage-hero' + (hasCover ? ' AvocadoUserPage-hero--hasCover' : ''),
    style: heroStyle
  }, m("div", {
    className: "AvocadoUserPage-cover"
  }), m("div", {
    className: "AvocadoUserPage-hero-inner"
  }, m("div", {
    className: "AvocadoUserPage-hero-bar"
  }, m("div", {
    className: `AvocadoUserPage-hero-avatarWrap${isOnline ? ' AvocadoUserPage-hero-avatarWrap--online' : ''}`,
    title: isOnline ? onlineLabel : undefined,
    "aria-label": isOnline ? onlineLabel : undefined
  }, isEditable ? m(AvatarEditor, {
    user: user
  }) : m(Avatar, {
    user: user,
    loading: "eager"
  })), m("div", {
    className: "AvocadoUserPage-hero-identity"
  }, m("div", {
    className: "AvocadoUserPage-hero-nameRow"
  }, m("h1", {
    className: "AvocadoUserPage-hero-name"
  }, user.displayName?.() || username), badges.length > 0 && m("ul", {
    className: "AvocadoUserPage-hero-badges badges"
  }, listItems(badges))), username && m("span", {
    className: "AvocadoUserPage-hero-username"
  }, "@", username), m("div", {
    className: "AvocadoUserPage-hero-meta"
  }, joinLabel && m("span", {
    className: "AvocadoUserPage-metaItem"
  }, m("i", {
    className: "far fa-calendar",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.profile.member_since', 'Member since {date}', {
    date: joinLabel
  })), !isOnline && lastSeen && m("span", {
    className: "AvocadoUserPage-metaItem"
  }, m("i", {
    className: "far fa-clock",
    "aria-hidden": "true"
  }), humanTime(lastSeen)))), controls.length > 0 && m("div", {
    className: "AvocadoUserPage-hero-actions"
  }, m(Dropdown, {
    buttonClassName: "Button AvocadoUserPage-controlsBtn",
    menuClassName: "Dropdown-menu--right",
    label: app.translator.trans('core.forum.user_controls.button')
  }, controls))), bioNode));
}

// ─── Shared nav builders ──────────────────────────────────────────────────────

/** Larguras dos rótulos das abas no esqueleto (posts, discussões, curtidas, menções…). */
const NAV_BONES = ['96px', '124px', '78px', '88px', '92px'];
export function buildSidebar(page) {
  const user = page?.user;
  if (!user) {
    // A lista e os botões de aba de verdade, com um osso no lugar de ícone + rótulo.
    return m("div", {
      className: "AvocadoUserPage-nav AvocadoUserPage-nav--skeleton",
      "aria-hidden": "true"
    }, m("ul", {
      className: "AvocadoUserPage-navInner"
    }, NAV_BONES.map(width => m("li", {
      key: width
    }, m("span", {
      className: "LinkButton"
    }, bone(width))))));
  }
  return m(ScrollableNav, null, m("ul", {
    className: "AvocadoUserPage-navInner"
  }, listItems(page.navItems().toArray())));
}
export function buildUserPhoneNav(page) {
  const user = page?.user;
  const items = user ? page.navItems().toArray() : [];
  return m("nav", {
    className: "IndexPage-nav sideNav"
  }, m("ul", null, m("li", {
    className: "item item-nav"
  }, m(SelectDropdown, {
    className: "App-titleControl",
    buttonClassName: "Button"
  }, items))));
}
flarum.reg.add('ramon-avocado', 'forum/components/UserProfileBuilders', { buildHero: buildHero,buildSidebar: buildSidebar,buildUserPhoneNav: buildUserPhoneNav, });